import React, { useState, useEffect, useRef } from 'react';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import apiService from '../api/api';

function AdminVoices() {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState(null);
  const voicesFetchRef = useRef(false);

  const [formData, setFormData] = useState({
    voice_name: '',
    file: null,
    tags: '',
  });

  // Fetch voices on mount
  useEffect(() => {
    if (voicesFetchRef.current) return;
    voicesFetchRef.current = true;
    fetchVoices();
  }, []);

  const fetchVoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminVoices();
      setVoices(data.voices || []);
    } catch (err) {
      setError(err.detail || 'Failed to fetch voices');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['audio/wav', 'audio/mpeg', 'audio/mp3', 'audio/x-wav'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid audio file (WAV or MP3)');
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setError(null);
      setFormData((prev) => ({
        ...prev,
        file: file,
      }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);

    if (!formData.voice_name.trim()) {
      setError('Please provide a voice name');
      return;
    }

    if (!formData.file) {
      setError('Please select an audio file');
      return;
    }

    setUploading(true);

    try {
      // Parse tags
      const tagsList = formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const response = await apiService.uploadAdminVoice(
        formData.file,
        formData.voice_name,
        JSON.stringify(tagsList)
      );

      if (response.status === 'success') {
        setSuccess('Voice uploaded successfully!');
        setFormData({ voice_name: '', file: null, tags: '' });
        // Reset file input
        const fileInput = document.getElementById('admin-voice-file');
        if (fileInput) fileInput.value = '';
        // Refresh voices list
        await fetchVoices();
      }
    } catch (err) {
      setError(err.detail || 'Failed to upload voice');
    } finally {
      setUploading(false);
    }
  };

  const openDeleteModal = (voice) => {
    setVoiceToDelete(voice);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setVoiceToDelete(null);
  };

  const handleConfirmDeleteVoice = async () => {
    if (!voiceToDelete) return;

    setDeleting(voiceToDelete.id);
    try {
      const response = await apiService.deleteAdminVoice(voiceToDelete.id);
      if (response.status === 'success') {
        setSuccess('Voice deleted successfully');
        await fetchVoices();
        closeDeleteModal();
      } else {
        setError(response.message || 'Failed to delete voice');
      }
    } catch (err) {
      setError(err.detail || 'Failed to delete voice');
    } finally {
      setDeleting(null);
    }
  };

  if (loading && voices.length === 0) {
    return (
      <div className="admin-voices-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading built-in voices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-voices-container">
      <div className="admin-voices-grid">
        {/* LEFT COLUMN - Built-in Voices List */}
        <div className="voices-list-column">
          <div className="voices-card">
            <div className="voices-header">
              <h2>Built-in Voices</h2>
              <span className="voice-count">{voices.length}</span>
            </div>

            {voices.length === 0 ? (
              <div className="empty-state">
                <p className="emoji">🎤</p>
                <p>No built-in voices yet. Upload one in the form on the right!</p>
              </div>
            ) : (
              <div className="admin-voices-list">
                {voices.map((voice) => (
                  <div key={voice.id} className="admin-voice-item">
                    <div className="voice-item-top">
                      <h4>{voice.name}</h4>
                      <button
                        className="btn-delete"
                        onClick={() => openDeleteModal(voice)}
                        disabled={deleting === voice.id}
                        title="Delete voice"
                      >
                        🗑️
                      </button>
                    </div>

                    <audio controls className="voice-player-small">
                      <source src={voice.audio_url} type="audio/wav" />
                      Your browser does not support the audio element.
                    </audio>

                    {voice.tags && voice.tags.length > 0 && (
                      <div className="voice-tags">
                        {voice.tags.map((tag, idx) => (
                          <span key={idx} className="tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="voice-item-meta">
                      <small>
                        {new Date(voice.created_at).toLocaleDateString()} at{' '}
                        {new Date(voice.created_at).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN - Upload Form */}
        <div className="upload-form-column">
          <div className="upload-card">
            <div className="section-header">
              <h3>📤 Upload New Voice</h3>
            </div>

            <form onSubmit={handleUpload} className="admin-upload-form">
              <div className="form-group">
                <label htmlFor="admin-voice-name">Voice Name *</label>
                <input
                  id="admin-voice-name"
                  type="text"
                  name="voice_name"
                  value={formData.voice_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Alex, Professional"
                  maxLength="100"
                  disabled={uploading}
                  className="form-input"
                />
                <small className="char-count">{formData.voice_name.length}/100</small>
              </div>

              <div className="form-group">
                <label htmlFor="admin-voice-file">Audio File (WAV/MP3, max 10MB) *</label>
                <div className="file-input-wrapper">
                  <input
                    id="admin-voice-file"
                    type="file"
                    accept="audio/wav,audio/mpeg,audio/mp3,audio/x-wav"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="file-input"
                  />
                  <span className="file-input-label">
                    {formData.file ? formData.file.name : '📁 Choose file...'}
                  </span>
                </div>
                {formData.file && (
                  <small className="file-size">
                    Size: {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="admin-voice-tags">Tags (comma-separated, optional)</label>
                <input
                  id="admin-voice-tags"
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="e.g., professional, calm, fast"
                  disabled={uploading}
                  className="form-input"
                />
                <small className="hint">Separate multiple tags with commas</small>
              </div>

              {error && (
                <div className="alert alert-error">
                  <span>❌ {error}</span>
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <span>✅ {success}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={uploading || !formData.voice_name.trim() || !formData.file}
                className="btn-upload"
              >
                {uploading ? '⏳ Uploading...' : '🚀 Upload Voice'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {voiceToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          title="Delete Built-in Voice?"
          message={`Delete "${voiceToDelete.name}"? This will remove the voice for all users.`}
          onConfirm={handleConfirmDeleteVoice}
          onCancel={closeDeleteModal}
          isLoading={deleting === voiceToDelete.id}
        />
      )}
    </div>
  );
}

export default AdminVoices;
